import { Navigate } from 'react-router-dom'

import { Box, Stack } from '@mui/material'

import { resolveHomeLanding } from 'routes'
import { useVeraSpaceStore } from 'stores'

import { Header } from 'components/navigation'
import { useAuth, useCanAccess, useChecklistVisibility, useCollapseChecklist, useHomeChecklist, useInternalRouteAccess, useIsInternalUser } from 'hooks'
import { useCurrentUserCompany } from 'hooks/api'

import { GettingStartedTours } from './components/GettingStartedTours'
import { HomeSections } from './components/HomeSections'
import { RecentActivity } from './components/RecentActivity'
import { SampleDataCard } from './components/SampleDataCard'
import { SetupChecklist } from './components/SetupChecklist'
import { HomePageSkeleton } from './HomePageSkeleton'

/**
 * Home — the first-run surface (VRG-338 / VRG-340).
 *
 * There is ONE Home. Same route, same layout, same welcome, for everyone. The setup
 * checklist BLOCK does not render for someone who cannot act on company setup; that is
 * conditional rendering of one block, not an "admin Home" and a "member Home".
 *
 * Two concerns, two scopes, deliberately not merged into one list:
 *   - setup checklist — COMPANY-scoped, derived from company state, gated on `manage_company`
 *   - tours          — USER-scoped, that person's own progress, reachable by everyone
 *
 * A Design hire joining a two-year-old brand has nothing to set up and everything to learn.
 * Conflating the two is what makes a checklist of un-actionable rows look reasonable.
 *
 * Tours appear exactly ONCE, in one of two forms chosen by whether the viewer has a checklist:
 * the standalone block when they do not (it is then their whole Home), a single Get-oriented
 * card when they do. See the comment at the render site.
 *
 * ## `/` IS THE ONE POST-AUTH DECISION POINT (VRG-339), and it now answers "stay here"
 *
 * The landing decision used to be duplicated across every sign-in path — OTP, password, the
 * WorkOS callback, password-setup — each re-deriving `isInternalUser` and picking a
 * destination, two of them with raw string literals, plus a fifth copy in a utils file with
 * ZERO call sites that returned a different answer from all of them. A sixth and seventh lived
 * in the router guards, and the sixth is what made first-run Home unreachable for months: a
 * cold load of `/` has no token yet, so ProtectedRoute bounced to /sign-in, the session then
 * minted one, and UnProtectedRoute sent the now-authenticated user to /certificates.
 *
 * They were not fixed by making every site call a smarter function. They were fixed by having
 * every site STOP DECIDING: each auth path navigates to `/`, and this page makes the one call.
 * That is not merely tidier — it is the only version that can work, because the answer depends
 * on server-side preferences and company setup state, none of which exists at the moment a
 * sign-in form submits.
 *
 * The role-based landing map that used to live in `utils/post-auth-redirect.ts` is DELETED
 * rather than kept: Home is permanent, so nobody is ever "sent on" from `/` except internal
 * staff and staff-in-space, which the two redirects below handle inline. Keeping an unused
 * landing helper around is exactly the fifth-copy problem VRG-339 existed to remove.
 */
export const HomePage = () => {
  const { profile } = useAuth()

  // VERA-876 (carried from the `HomeRedirect` this page replaces — App.tsx:127-150).
  // While a Vera staffer is inside a company's space they operate AS a brand user, so `/`
  // resolves to the brand surface, not the internal console. Deleting either branch strands
  // internal staff on a brand getting-started page.
  const inSpace = useVeraSpaceStore((s) => !!s.companyId)
  // VRG-410 — `useIsInternalUser`, not an inline pair read off `profile`. TWO bugs in
  // one line: it missed `vera_fulfillment` / `vera_finance`, and `profile.role.name` is
  // the MAPPED name, which falls back to `'company_team_member'` for any role outside
  // `mapWorkOSUserToProfile`'s four-entry table — so those two roles could not have been
  // recognised here even once they were added. The hook reads the raw WorkOS role and
  // carries the same `!inSpace` clause this line had.
  const isInternalUser = useIsInternalUser()

  // `action`, never `role`: role checks return false for a staffer in space
  // (useCanAccess.ts:22-33), which would render an empty checklist rather than none at all.
  const { isHasAccess: canManageCompany } = useCanAccess({ action: 'manage_company' })

  const { data: company, isLoading: companyLoading } = useCurrentUserCompany({ enabled: !isInternalUser })
  const checklist = useHomeChecklist({ enabled: !isInternalUser && canManageCompany })
  const { isChecklistLive, isLoading: visibilityLoading } = useChecklistVisibility()
  const { isCollapsed, toggle, isToggling } = useCollapseChecklist()

  // VRG-661: the two redirects (staff → internal console, staff-in-space → brand
  // Settings) now live in `resolveHomeLanding` so the decision is guard-tested. Staff land
  // on internal Analytics, or Companies when their role cannot open Analytics.
  const { canReachInternalPath } = useInternalRouteAccess()
  // VRG-677: "Return to Internal" from the space home renders `/` once more with the space
  // gone; `leaving` keeps this redirect from racing the banner's navigation to Analytics.
  const leaving = useVeraSpaceStore((s) => s.leaving)
  const landing = resolveHomeLanding({ isInternalRole: isInternalUser, inSpace, canReachInternalPath, leaving })
  if (landing) return <Navigate to={landing} replace />

  if (companyLoading || visibilityLoading || (canManageCompany && checklist.isLoading)) {
    return <HomePageSkeleton showChecklist={canManageCompany} />
  }

  /*
    NO REDIRECT HERE ANY MORE. `/` used to bounce to a role-based surface once Home retired;
    Home is PERMANENT now, so a company user who lands here stays here whether or not they
    have finished setting up. The two redirects above survive because they are about WHO the
    viewer is (internal staff, staff-in-space), not about how far through onboarding they are.

    VRG-339's point still holds and is in fact strengthened: `/` remains the ONE place the
    post-auth destination is decided, and every sign-in path lands here rather than
    re-deriving an answer. The answer is now simply "you are already there".
  */

  const firstName = profile?.first_name?.trim()
  const brand = company?.name?.trim()
  // The checklist block renders only for someone who can act on company setup AND has not
  // finished or dismissed it. Hoisted because the header's copy and its Dismiss button both
  // depend on the same answer.
  const showChecklist = canManageCompany && isChecklistLive

  return (
    /*
      A READING COLUMN, not a data surface. The rest of the platform is tables that want every
      pixel of width; this page is a short list of sentences, and at full width on a wide
      monitor its rows stretched far enough that the tick and the chevron sat at opposite edges
      with a void between them. The cap wraps the HEADER TOO — centring only the cards would
      leave the title hard left above a centred column, which reads as a layout bug.

      ⚠️ DELIBERATE EXCEPTION TO §29's HEADER DATUM. That rule says a page header's first row
      centres on the sidebar logo line, that `Header` computes the offset itself, and that you
      must NEVER nudge it with a per-page `pt`. This does exactly that, on purpose and for one
      page only.

      The reasoning: the datum exists so that moving between WORKSPACE pages does not jog the
      title line. Home is not one of those — it is a welcome that appears once and retires, and
      it is the only page whose job is to feel like an arrival rather than a tool. Dropping it
      down the fold is the cheapest way to say that.

      The cost is real and is accepted: on this page the title no longer lines up with the VERA
      mark in the sidebar. If a second page ever wants this treatment, it stops being an
      exception and belongs in `Header` as a variant — do not copy this `pt` to get it.

      The page wordmark that briefly sat here has been REMOVED: the sidebar renders the same
      mark roughly level with it, so the two read as one mark duplicated rather than as a
      welcome.
    */
    <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', pt: 10 }}>
      <Header
        title={firstName ? `Welcome, ${firstName}` : 'Welcome to Vera'}
        /*
          The description follows the checklist, because the page no longer "steps aside once
          you're done" — that sentence was written when Home retired and is now a promise the
          product does not keep. While setup is outstanding it says what to do; afterwards it
          describes what the page is for.
        */
        description={
          showChecklist
            ? brand
              ? `Get ${brand} set up and learn your way around.`
              : 'Get set up and learn your way around.'
            : brand
              ? `What's happening at ${brand}, and where to find things.`
              : "What's happening, and where to find things."
        }
        // Dismiss moved INTO the checklist card — it only ever affected that block, and a
        // control in the page header implied it dismissed the page.
      />
      <Stack spacing={4}>
        {/* The checklist BLOCK is absent for someone who cannot act on company setup, AND
            once it has retired (completed or dismissed). Home itself no longer goes anywhere
            — the page outlived the scaffolding it was built around. */}
        {showChecklist && (
          <SetupChecklist
            blocks={checklist.blocks}
            moreItems={checklist.moreItems}
            completedCount={checklist.completedCount}
            total={checklist.total}
            isCollapsed={isCollapsed}
            onToggleCollapsed={toggle}
            isToggling={isToggling}
            isSingleSeat={checklist.isSingleSeat}
          />
        )}
        {/*
          TOURS APPEAR EXACTLY ONCE, and which form they take depends on whether this person
          has a checklist. One condition drives both halves — passed down rather than
          re-derived in HomeSections — so they cannot both render or both vanish.

          WITH a checklist (an admin): tours are the secondary concern on a page whose main
          job is the six setup steps, and the standalone block holds a single tour today. It
          collapses to one card in Get oriented.

          WITHOUT a checklist (an invited Design hire at an established brand — nothing to set
          up, everything to learn): the block IS their Home. Collapsing it would leave them a
          welcome line and three tiles, which is the exact outcome the two-block split was
          designed to prevent.

          Revisit when VRG-228 authors the rest of the catalog: a card pointing at a dialog
          reads thin once there are six or eight tours, and the admin case may want the block
          back.
        */}
        {!canManageCompany && <GettingStartedTours />}
        {/*
          VRG-512 — sample data's whole lifecycle in one card: the offer to fill an empty
          console, and, once filled, the way back out. This is the FIRST of intro data's
          two entry points (the other is the confirm on a record-spotlighting tour) and
          now also its exit.

          ONE card, not two, and one branch inside it: the seed offer and the clear
          control are the two arms of a single ternary, so "exactly one of them shows"
          is structural rather than a pair of guards someone has to keep complementary.

          Self-suppressing in the third case: an established brand (real products, no
          samples) gets no card and no skeleton, so the block costs them no layout at
          all. It renders nothing until the status query settles, so neither arm flashes.

          Below the tours rather than above them: a tour is the thing we most want a new
          user to take, and the sample data exists to serve the tour rather than the other
          way round. Above `RecentActivity` because that block is empty for exactly the
          user this card is for.

          NOT gated on `canManageCompany`. Seeding and clearing are unpermissioned by
          design (see the controller's own argument — the rows are labelled, unbillable,
          and removable by the same user who added them), and an invited Design hire
          landing on an empty console is precisely who benefits.
        */}
        <SampleDataCard />
        {/* The block that earns a permanent Home: the only one here telling the viewer
            something they did not already know. Renders nothing at all when there is no
            activity, rather than reporting emptiness in the page's best space. */}
        <RecentActivity />
        {/* Orientation + account, below the blocks that carry the actual onboarding. */}
        <HomeSections hasChecklist={canManageCompany} />
      </Stack>
    </Box>
  )
}
